import { Button } from "../components/button/Button";
import { ButtonProps } from "../components/button/ButtonProps";
import { Component, Textbox } from "..";
import { ClickEvent, ClickListener } from "../events/ClickEvent";
import { DesignableObjectTempState } from "../base/DesignableObjectTempState";

export class ResetFormButton extends Button {
    private defaultStateMap: Map<Component, DesignableObjectTempState>;
    public doAfterReset: ClickListener;

    constructor(props?: Partial<ButtonProps>) {
        super({
            color: "subtle",
            borderWidth: 0,
            caption: "Reset",
            id: "buttonReset",
            ...props
        });
        this.addClickListener(this.onClick);
    }

    private onClick = async (event: ClickEvent) => {
        try {
            this.busy = true;
            await this.dataSource.resetSearchRow();
            this.resetNonDataComponentState();
            this.applyDefaultState();
            this.doAfterReset?.(event);
        } finally {
            this.busy = false;
        }
    }

    /**
     * This method is called after the outermost layout (the layout that isn't nested) is loaded.  This allows the
     * default state map to be populated after the enclosing layout has been loaded, which in turn allows the map to
     * include component property values set during the layout's onLoad() method.
     */
    override _doAfterOutermostLayoutLoaded() {
        this._loadDefaultStateMap();
    }

    _loadDefaultStateMap() {
        this.defaultStateMap = new Map();
        const searchComponents = this.dataSource?.boundSearchComponents ?? [];
        for (const searchComponent of searchComponents) {
            const defaultStateProperties = this.getDefaultStateProperties(searchComponent);
            const tempState = searchComponent.createStateObject({}, defaultStateProperties);
            this.defaultStateMap.set(searchComponent, tempState);
        }
    }

    private getDefaultStateProperties(component: Component): string[] {
        const result = [ "enabled", "visible" ];
        switch (component.serializationName) {
            case "textbox":
                result.push("text", "dateDefault");
                if ((component as Textbox).items != null)
                    result.push("selectedItem");
                break;
            case "checkbox":
            case "switch":
                result.push("checked");
                break;
            default:
                break;
        }
        return result;
    }

    private applyDefaultState() {
        if (this.defaultStateMap == null)
            return;
        for (const entry of this.defaultStateMap.entries()) {
            const searchComponent = entry[0];
            const defaultState = entry[1];
            searchComponent.setProps(defaultState?.originalProps);
        }
    }

    /**
     * This method is called to allow each component to reset any part of itself that would not be affected by the reset
     * of its displayed data.
     *
     * Some of the logic in getDefaultStateProperties() could probably be moved here.
     */
    private resetNonDataComponentState() {
        this.dataSource.boundSearchComponents.forEach(component => component.resetNonDataState());
    }
}
